import SunCalc from 'suncalc';
import moment from 'moment-timezone';
import { RRule } from 'rrule';

export const ASTRO_EVENTS = [
	{ key: 'sunrise', label: 'Sunrise' },
	{ key: 'sunriseEnd', label: 'Sunrise end' },
	{ key: 'goldenHourEnd', label: 'Golden hour (morning)' },
	{ key: 'solarNoon', label: 'Solar noon' },
	{ key: 'goldenHour', label: 'Golden hour (evening)' },
	{ key: 'sunsetStart', label: 'Sunset start' },
	{ key: 'sunset', label: 'Sunset' },
	{ key: 'dawn', label: 'Civil dawn' },
	{ key: 'dusk', label: 'Civil dusk' },
	{ key: 'nauticalDawn', label: 'Nautical dawn' },
	{ key: 'nauticalDusk', label: 'Nautical dusk' },
	{ key: 'nightEnd', label: 'Astronomical dawn' },
	{ key: 'night', label: 'Astronomical dusk' },
];

export const ASTRO_EVENT_KEYS = ASTRO_EVENTS.map((e) => e.key);

const DEFAULT_TIMEZONE = 'America/Edmonton';

const FREQ_MAP = {
	0: RRule.YEARLY,
	1: RRule.MONTHLY,
	2: RRule.WEEKLY,
	3: RRule.DAILY,
	4: RRule.HOURLY,
	5: RRule.MINUTELY,
	6: RRule.SECONDLY,
};

export function isAstroSchedule(schedule) {
	return schedule?.timeMode === 'astro';
}

export function hasAstroLocation(system) {
	if (!system) return false;
	const lat = system.latitude;
	const lng = system.longitude;
	return typeof lat === 'number' && !Number.isNaN(lat) &&
		typeof lng === 'number' && !Number.isNaN(lng);
}

export function getSystemTimezone(system) {
	return system?.timezone || DEFAULT_TIMEZONE;
}

export function toLocalDayMoment(dateInput, timezone) {
	if (moment.isMoment(dateInput)) {
		return dateInput.clone().tz(timezone).startOf('day');
	}
	if (dateInput instanceof Date) {
		return moment.tz(dateInput, timezone).startOf('day');
	}
	return moment.tz(String(dateInput), 'YYYY-MM-DD', timezone).startOf('day');
}

export function computeAstroTimes(dateInput, lat, lng, timezone) {
	const day = toLocalDayMoment(dateInput, timezone);
	const calcDate = day.clone().hour(12).minute(0).second(0).millisecond(0).toDate();
	const raw = SunCalc.getTimes(calcDate, lat, lng);
	const result = {};

	for (const key of ASTRO_EVENT_KEYS) {
		const t = raw[key];
		if (t instanceof Date && !Number.isNaN(t.getTime())) {
			result[key] = t;
		} else {
			result[key] = null;
		}
	}
	return result;
}

export function formatAstroLabel(event, offsetMinutes) {
	const ev = ASTRO_EVENTS.find((e) => e.key === event);
	const name = ev ? ev.label : event;
	const offset = Number(offsetMinutes) || 0;
	if (offset === 0) return name;
	const sign = offset > 0 ? '+' : '\u2212';
	return `${name} ${sign}${Math.abs(offset)} min`;
}

export function formatTimeInTz(date, timezone) {
	if (!date) return '';
	return moment.tz(date, timezone).format('h:mm A');
}

function isRepeating(schedule) {
	if (schedule.repeats === true) return true;
	if (schedule.repeats === false) return false;
	const r = schedule.r_rule || schedule.rrule;
	return r != null && r.freq != null && r.freq !== '';
}

function performAtMillis(schedule) {
	const performAt = schedule.performAt;
	if (!performAt) return null;
	if (typeof performAt.toDate === 'function') return performAt.toDate().getTime();
	if (typeof performAt.toMillis === 'function') return performAt.toMillis();
	const seconds = performAt.seconds ?? performAt._seconds;
	if (seconds != null) {
		const nanos = performAt.nanoseconds ?? performAt._nanoseconds ?? 0;
		return seconds * 1000 + Math.floor(nanos / 1e6);
	}
	if (performAt instanceof Date) return performAt.getTime();
	const parsed = new Date(performAt).getTime();
	return Number.isNaN(parsed) ? null : parsed;
}

function prepareRRuleOpts(schedule, dtstart) {
	const repeatRule = schedule.r_rule || schedule.rrule;
	if (!repeatRule) return null;
	const rruleOpts = { ...(repeatRule.state || repeatRule) };
	delete rruleOpts.dtstart;
	delete rruleOpts.runmode;
	if (dtstart) rruleOpts.dtstart = dtstart;
	if (rruleOpts.freq !== undefined) {
		const f = Number(rruleOpts.freq);
		rruleOpts.freq = FREQ_MAP[f] || f;
	}
	if (rruleOpts.interval !== undefined) {
		rruleOpts.interval = +rruleOpts.interval || 1;
	}
	if (Array.isArray(rruleOpts.byweekday) && rruleOpts.byweekday.length === 0) delete rruleOpts.byweekday;
	if (Array.isArray(rruleOpts.bymonth) && rruleOpts.bymonth.length === 0) delete rruleOpts.bymonth;
	return rruleOpts;
}

export function findNextAstroTargetDate(schedule, fromDateInput, systemTz) {
	const fromDay = toLocalDayMoment(fromDateInput, systemTz);

	if (!isRepeating(schedule)) {
		const performAtMs = performAtMillis(schedule);
		if (performAtMs != null) {
			return moment.tz(performAtMs, systemTz).startOf('day');
		}
		return fromDay;
	}

	const repeatRule = schedule.r_rule || schedule.rrule;
	if (!repeatRule) return fromDay;

	try {
		const anchor = fromDay.clone().hour(12).minute(0).second(0).millisecond(0).toDate();
		const opts = prepareRRuleOpts(schedule, anchor);
		if (!opts) return fromDay;
		const rule = new RRule(opts);
		const searchStart = fromDay.toDate();
		const searchEnd = fromDay.clone().add(400, 'days').endOf('day').toDate();
		const occs = rule.between(searchStart, searchEnd, true);
		if (occs.length > 0) {
			return moment.tz(occs[0], systemTz).startOf('day');
		}
	} catch (e) {
		// fall through
	}
	return fromDay;
}

export function resolveAstroPerformAt(schedule, system, targetDateInput) {
	if (!isAstroSchedule(schedule) || !hasAstroLocation(system)) return null;

	const tz = getSystemTimezone(system);
	const event = schedule.astroEvent || 'sunset';
	const offset = Number(schedule.astroOffsetMinutes) || 0;
	const targetDay = toLocalDayMoment(targetDateInput, tz);
	const times = computeAstroTimes(targetDay, system.latitude, system.longitude, tz);
	const base = times[event];
	if (!base) return null;
	return moment(base).add(offset, 'minutes').toDate();
}

export function resolveAstroTargetDateForRefresh(schedule, system, todayInput) {
	const tz = getSystemTimezone(system);
	const today = toLocalDayMoment(todayInput, system?.timezone || tz);

	if (!isRepeating(schedule)) {
		const performAtMs = performAtMillis(schedule);
		if (performAtMs == null) return today;
		return moment.tz(performAtMs, tz).startOf('day');
	}

	return findNextAstroTargetDate(schedule, today, tz);
}

export function getLocalDateString(dateInput, timezone) {
	return toLocalDayMoment(dateInput, timezone).format('YYYY-MM-DD');
}

export function getPrimaryAstroBanner(times, timezone) {
	if (!times) return '';
	const parts = [];
	const primary = [
		{ key: 'sunrise', label: 'Sunrise' },
		{ key: 'solarNoon', label: 'Solar noon' },
		{ key: 'sunset', label: 'Sunset' },
	];
	for (const { key, label } of primary) {
		const t = times[key];
		if (t) parts.push(`${label} ${formatTimeInTz(t, timezone)}`);
	}
	return parts.join(' \u00b7 ');
}

export function getAstroTimeForDay(schedule, system, dayMoment) {
	if (!isAstroSchedule(schedule) || !hasAstroLocation(system)) return null;
	const tz = getSystemTimezone(system);
	const day = moment.isMoment(dayMoment) ? dayMoment : moment(dayMoment);
	return resolveAstroPerformAt(schedule, system, day.tz(tz).format('YYYY-MM-DD'));
}

export function getAstroTimesForDisplay(system, dateInput) {
	if (!hasAstroLocation(system)) return null;
	const tz = getSystemTimezone(system);
	const dateStr = getLocalDateString(dateInput, tz);
	const cached = system.astroTimes;
	if (cached?.date === dateStr && cached?.times) {
		const result = {};
		for (const key of ASTRO_EVENT_KEYS) {
			const val = cached.times[key];
			if (val?.toDate) result[key] = val.toDate();
			else if (val?.seconds != null) result[key] = new Date(val.seconds * 1000);
			else if (val instanceof Date) result[key] = val;
			else result[key] = null;
		}
		return result;
	}
	return computeAstroTimes(dateInput, system.latitude, system.longitude, tz);
}
